import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { uiPack } from './locales/uiPack';
import { deepMerge } from './locales/deepMerge';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        login: 'Login',
        signup: 'Get Started',
        logout: 'Logout'
      },
      
      // Landing Page
      landing: {
        hero: {
          badge: 'Enterprise Security Solution',
          title: 'Quiet, reliable access for every team.',
          description: 'Give people a secure path to company resources — with central control and less day-to-day friction.',
          cta: 'Start free trial',
          demo: 'Watch Demo',
          stat1: 'Uptime',
          stat2: 'Active Users',
          stat3: 'Encryption'
        },
        features: {
          title: 'Built for operations, not theatre.',
          subtitle: 'Encryption, speed, and reach — without the clutter.',
          security: {
            title: 'Military-Grade Encryption',
            description: 'Your data is completely secure with AES-256 bit encryption standard. We protect your privacy with zero-log policy.'
          },
          speed: {
            title: 'Ultra-Fast Connection',
            description: 'We provide seamless and high-speed data transfer with optimized server infrastructure.'
          },
          global: {
            title: 'Global Network',
            description: 'Fast access from anywhere with our servers in strategic locations worldwide.'
          }
        },
        platforms: {
          title: 'Use on All Platforms',
          subtitle: 'Seamless protection on all your devices'
        },
        map: {
          title: 'Global Server Network',
          subtitle: 'Serving with 5000+ servers in 50+ countries',
          countries: 'Countries',
          servers: 'Servers',
          cities: 'Cities'
        },
        devices: {
          title: 'Protect All Your Devices at Once',
          subtitle: 'Connect up to 10 devices simultaneously with a single account. Stay safe on all your devices from your computer to your phone.',
          features: {
            unlimited: 'Unlimited bandwidth',
            auto: 'Automatic connection protection',
            split: 'Split tunneling support',
            killswitch: 'Kill switch feature'
          }
        },
        useCases: {
          title: 'Use Cases',
          subtitle: 'Flexible solutions for different scenarios',
          case1: {
            title: 'Corporate Remote Access',
            description: 'Secure and uninterrupted connection for remote employees working from home'
          },
          case2: {
            title: 'Factory / Branch Connections',
            description: 'Secure data transfer and network management between different locations'
          },
          case3: {
            title: 'Mobile Access',
            description: 'Access corporate resources from anywhere via tablet and phone'
          }
        },
        cta: {
          title: 'Start Building Your Secure Network Now',
          description: 'Discover all features with a 14-day free trial',
          button: 'Get Started'
        },
        footer: {
          tagline: 'Enterprise-grade security,\nTurkish software assurance',
          product: 'Product',
          features: 'Features',
          pricing: 'Pricing',
          docs: 'Documentation',
          company: 'Company',
          about: 'About',
          contact: 'Contact',
          careers: 'Careers',
          support: 'Support',
          help: 'Help Center',
          status: 'System Status',
          updates: 'Updates',
          rights: '© 2025 ArcVPN. All rights reserved.'
        }
      },
      
      // Login Page
      login: {
        title: 'Welcome Back',
        subtitle: 'Sign in to your account',
        sessionExpired: 'Your session has expired. Please login again.',
        email: 'Email Address',
        emailPlaceholder: 'example@company.com',
        password: 'Password',
        passwordPlaceholder: 'Your password',
        forgotPassword: 'Forgot password?',
        loginButton: 'Sign In',
        loading: 'Signing in...',
        noAccount: "Don't have an account?",
        signupLink: 'Sign up for free',
        branding: {
          title: 'Enter the tunnel.',
          description: 'Sign in to manage peers, nodes, and company access from one control plane.',
          stat1: 'Companies',
          stat2: 'Uptime',
          stat3: 'Servers'
        },
        forgot: {
          title: 'Reset access',
          description: 'Enter your account email. We will guide you through the next step.',
          submit: 'Continue',
          back: 'Back to sign in',
          needEmail: 'Please enter your email address',
          success: 'Thanks. If an account exists for {{email}}, our team can help restore access. You can also reach support from Contact.',
          contact: 'Contact support'
        },
        errors: {
          fillAll: 'Please fill in all fields',
          failed: 'Login failed'
        },
        '2fa': {
          title: 'Two-Factor Authentication',
          description: 'Enter the 6-digit code sent to your email address',
          back: 'Back',
          invalidCode: 'Please enter the 6-digit code',
          verifyError: 'Verification failed',
          resendError: 'Failed to send code',
          verifying: 'Verifying...',
          verify: 'Verify',
          noCode: "Didn't receive the code?",
          resendIn: 'Resend ({{seconds}}s)',
          resend: 'Resend Code'
        }
      },
      
      // Signup Page
      signup: {
        title: 'Create Account',
        subtitle: 'Enter your details to get started',
        fullName: 'Full Name',
        fullNamePlaceholder: 'Your full name',
        email: 'Email Address',
        emailPlaceholder: 'example@company.com',
        companyName: 'Company Name',
        companyNamePlaceholder: 'Your company name',
        inviteCodeToggle: 'I have a company invite code',
        inviteCode: 'Invite Code',
        inviteCodePlaceholder: 'Enter your invite code',
        password: 'Password',
        passwordPlaceholder: 'At least 8 characters',
        confirmPassword: 'Confirm Password',
        confirmPasswordPlaceholder: 'Re-enter your password',
        signupButton: 'Create Account',
        loading: 'Processing...',
        hasAccount: 'Already have an account?',
        loginLink: 'Sign In',
        branding: {
          title: 'Welcome to Your Secure Network',
          description: 'Enterprise-grade VPN security in one platform. Start protecting your team today.',
          feature1: '14-day free trial',
          feature2: 'No credit card required',
          feature3: 'Cancel anytime'
        },
        errors: {
          fillAll: 'Please fill in all fields',
          companyRequired: 'Company name required (or use invite code)',
          inviteRequired: 'Please enter invite code',
          passwordMismatch: 'Passwords do not match',
          passwordLength: 'Password must be at least 8 characters',
          failed: 'Signup failed'
        }
      },
      
      // Public Pages
      features: {
        title: 'Features',
        subtitle: 'Everything you need for secure connectivity',
        list: {
          encryption: {
            title: 'AES-256 Bit Encryption',
            description: 'Your data is completely secure with military-grade encryption. We guarantee your privacy with zero-log policy.',
            detail1: 'End-to-end encryption',
            detail2: 'Perfect forward secrecy',
            detail3: 'Zero logging policy'
          },
          speed: {
            title: 'Ultra-Fast Connection',
            description: 'Seamless and high-speed data transfer with optimized server infrastructure.',
            detail1: '10Gbps servers',
            detail2: 'Low latency',
            detail3: 'Unlimited bandwidth'
          },
          global: {
            title: 'Global Server Network',
            description: 'Fast access from anywhere in the world with 5000+ servers in 50+ countries.',
            detail1: '5000+ servers',
            detail2: '50+ countries',
            detail3: '100+ cities'
          },
          multiplatform: {
            title: 'Multi-Platform Support',
            description: 'Native applications for Windows, macOS, Linux, iOS and Android.',
            detail1: 'Up to 10 devices',
            detail2: 'All platforms',
            detail3: 'Easy setup'
          },
          mfa: {
            title: 'Multi-Factor Authentication',
            description: 'Additional security layer with multi-factor authentication.',
            detail1: '2FA/MFA support',
            detail2: 'Hardware token',
            detail3: 'Biometric login'
          },
          admin: {
            title: 'Centralized Management Panel',
            description: 'Manage all your users from a single place with web-based admin panel.',
            detail1: 'User management',
            detail2: 'Detailed logs',
            detail3: 'Real-time monitoring'
          },
          killswitch: {
            title: 'Automatic Kill Switch',
            description: 'Automatically cuts internet access when VPN connection drops.',
            detail1: 'Data security',
            detail2: 'IP leak protection',
            detail3: 'DNS leak protection'
          },
          split: {
            title: 'Split Tunneling',
            description: 'Choose which applications use the VPN.',
            detail1: 'Application-based',
            detail2: 'Flexible configuration',
            detail3: 'Performance optimization'
          },
          local: {
            title: 'Turkish Software',
            description: 'Developed in Turkey, domestic and national software assurance.',
            detail1: 'Local support',
            detail2: 'Quick response',
            detail3: 'Custom solutions'
          }
        },
        cta: {
          title: 'Start Your Secure Network Now',
          description: '14-day free trial, no credit card required'
        }
      },
      pricing: {
        title: 'Pricing',
        subtitle: 'Choose the best plan for your business. All plans start with a 14-day free trial.',
        popular: 'Most Popular',
        startBtn: 'Get Started',
        contactBtn: 'Contact Us',
        plans: {
          starter: {
            name: 'Starter',
            period: 'month',
            description: 'Ideal starter package for small teams',
            feature1: 'Up to 5 users',
            feature2: 'Basic VPN features',
            feature3: 'Email support',
            feature4: '50 GB/month traffic',
            feature5: '10 device connections',
            feature6: 'Basic reporting'
          },
          professional: {
            name: 'Professional',
            period: 'month',
            description: 'Comprehensive solution for growing companies',
            feature1: 'Up to 25 users',
            feature2: 'All VPN features',
            feature3: 'Priority support',
            feature4: 'Unlimited traffic',
            feature5: 'Unlimited devices',
            feature6: 'Advanced reporting',
            feature7: 'API access',
            feature8: 'SSO integration'
          },
          enterprise: {
            name: 'Enterprise',
            price: 'Custom',
            period: 'quote',
            description: 'Custom solutions for enterprise companies',
            feature1: 'Unlimited users',
            feature2: 'All features',
            feature3: '24/7 support',
            feature4: 'Dedicated server',
            feature5: 'SLA guarantee',
            feature6: 'Custom integrations',
            feature7: 'Training & consulting',
            feature8: 'On-premise installation'
          }
        },
        faq: {
          title: 'Frequently Asked Questions',
          q1: 'How does the free trial work?',
          a1: 'You can try all features free for 14 days. No credit card required.',
          q2: 'Can I cancel anytime?',
          a2: 'Yes, you can cancel anytime without any commitment.',
          q3: 'What currency are invoices issued in?',
          a3: 'All invoices are issued in Turkish Lira (₺). VAT included.',
          q4: 'Can I upgrade my plan?',
          a4: 'Yes, you can upgrade or downgrade your plan anytime.'
        }
      },
      docs: {
        title: 'Documentation',
        subtitle: 'Complete guides and API references'
      },
      about: {
        title: 'About Us',
        subtitle: 'Building the future of secure connectivity'
      },
      contact: {
        title: 'Contact',
        subtitle: 'Get in touch with our team',
        formSubmitted: 'Form submitted! (Backend integration required)'
      },
      careers: {
        title: 'Careers',
        subtitle: 'Join our growing team'
      },
      help: {
        title: 'Help Center',
        subtitle: 'Find answers to common questions'
      },
      status: {
        title: 'System Status',
        subtitle: 'Real-time status of our services'
      },
      updates: {
        title: 'Updates',
        subtitle: 'Latest features and improvements'
      },
      
      // Email Verification
      verification: {
        title: 'Email Verification',
        subtitle: 'Verify your email address to activate your account',
        instruction: 'We sent a verification email. Please click the link in your email or enter the code below.',
        codeLabel: 'Verification Code',
        codePlaceholder: 'Enter verification code',
        verifyBtn: 'Verify',
        verifying: 'Verifying...',
        or: 'or',
        didntReceive: 'Didn\'t receive email?',
        resendBtn: 'Resend',
        sending: 'Sending...',
        checkBtn: 'I Confirmed, Check',
        backToDashboard: 'Back to Dashboard',
        backToLogin: 'Back to Login',
        success: 'Email verified successfully! Redirecting...',
        resent: 'Verification email sent again!',
        checking: 'Checking verification status...',
        errors: {
          emptyCode: 'Please enter the verification code',
          failed: 'Verification failed',
          noUser: 'User information not found',
          sendFailed: 'Could not send email',
          refresh: 'Please refresh the page and try again'
        }
      },
      
      // Dashboard
      dashboard: {
        welcome: 'Welcome',
        subtitle: 'Overview of your VPN network',
        recentActivities: 'Recent Activities',
        systemInfo: 'System Information',
        quickAccess: 'Quick Access',
        stats: {
          connections: 'Active Connections',
          users: 'Total Users',
          data: 'Data Transfer',
          status: 'System Status',
          online: 'Online'
        },
        activities: {
          connected: 'Connection started',
          userAdded: 'New user added',
          disconnected: 'Connection ended',
          systemUpdated: 'System updated'
        },
        system: {
          version: 'Server Version',
          uptime: 'Uptime',
          cpu: 'CPU Usage',
          memory: 'Memory Usage',
          disk: 'Disk Usage'
        },
        time: {
          minutesAgo: '{{count}} minutes ago',
          hoursAgo: '{{count}} hours ago',
          days: 'days',
          hoursShort: 'hrs'
        },
        actions: {
          addUser: 'Add New User',
          viewReports: 'View Reports',
          settings: 'System Settings'
        },
        verification: {
          title: 'Email Verification Required',
          message: 'Please verify your email address to access the dashboard.',
          button: 'Verify Now'
        }
      },

      // System Admin
      systemAdmin: {
        title: 'System Management',
        subtitle: 'ARCVPN system management panel',
        back: 'Back',
        menu: {
          companies: {
            title: 'Company Management',
            description: 'Company information, licenses and user management'
          },
          servers: {
            title: 'VPN Servers',
            description: 'VPN server management, locations and node configuration'
          },
          settings: {
            title: 'System Settings',
            description: 'Security, network, notification and system configurations'
          },
          logs: {
            title: 'System Logs',
            description: 'System logs, activity tracking and monitoring'
          }
        },
        servers: {
          title: 'VPN Servers',
          subtitle: 'Server management and monitoring',
          stats: {
            totalServers: 'Total Servers',
            onlineServers: 'Online Servers',
            activeUsers: 'Active Users',
            avgLoad: 'Average Load'
          },
          filter: {
            label: 'Country Filter',
            all: 'All Countries'
          },
          pendingServers: 'Pending Servers',
          addServer: 'Add New Server',
          card: {
            load: 'Load',
            users: 'Users',
            monitor: 'Monitor',
            settings: 'Settings',
            restart: 'Restart',
            approve: 'Approve',
            reject: 'Reject'
          },
          status: {
            online: 'Online',
            offline: 'Offline',
            maintenance: 'Maintenance'
          },
          agentStatus: {
            connected: 'Agent Connected',
            disconnected: 'Agent Disconnected',
            pending: 'Pending Approval'
          },
          empty: {
            title: 'No servers yet',
            description: 'Use the "Add New Server" button to add a new server.'
          },
          loading: 'Loading servers...',
          error: 'An error occurred while loading servers',
          retry: 'Try Again',
          pendingModal: {
            title: 'Pending Servers',
            noPending: 'No pending servers.',
            approve: 'Approve',
            reject: 'Reject'
          }
        }
      },

      // Sidebar
      sidebar: {
        dashboard: 'Dashboard',
        connections: 'Connections',
        downloads: 'Downloads',
        company: 'My Company',
        admin: 'System Admin',
        profile: 'Profile',
        logout: 'Logout'
      },

      // Connections Page
      connections: {
        title: 'Connections',
        myConnections: 'My Connections',
        companyConnections: 'Company Connections',
        activeConnections: 'Active Connections',
        totalData: 'Total Data',
        avgDuration: 'Avg. Duration',
        loading: 'Loading...',
        noData: 'No active connections found',
        table: {
          user: 'User',
          server: 'Server',
          ipAddress: 'IP Address',
          location: 'Location',
          startTime: 'Start Time',
          duration: 'Duration',
          data: 'Data',
          status: 'Status'
        },
        status: {
          active: 'Active',
          disconnected: 'Disconnected'
        },
        footer: 'Showing {{count}} connections',
        time: {
          hours: 'h',
          minutes: 'm'
        }
      },

      // Downloads Page
      downloads: {
        title: 'Download ArcVPN',
        subtitle: 'Choose your platform and start protecting your privacy',
        download: 'Download',
        comingSoon: '{{platform}} client coming soon!',
        platforms: {
          windows: {
            name: 'Windows',
            description: 'Windows 10/11 (64-bit)'
          },
          macos: {
            name: 'macOS',
            description: 'macOS 11.0 or later'
          },
          linux: {
            name: 'Linux',
            description: 'Ubuntu, Debian, Fedora'
          },
          android: {
            name: 'Android',
            description: 'Android 8.0 or later'
          },
          ios: {
            name: 'iOS',
            description: 'iOS 14.0 or later'
          }
        },
        guide: {
          title: 'Installation Guide',
          step1: {
            title: 'Download',
            description: 'Choose your platform and download the installer'
          },
          step2: {
            title: 'Install',
            description: 'Run the installer and follow the setup wizard'
          },
          step3: {
            title: 'Login',
            description: 'Open the app and login with your credentials'
          },
          step4: {
            title: 'Connect',
            description: 'Choose a server and start browsing securely'
          }
        },
        requirements: {
          title: 'System Requirements',
          windows: {
            title: 'Windows',
            req1: 'Windows 10 or later (64-bit)',
            req2: '4 GB RAM minimum',
            req3: '100 MB free disk space'
          },
          macos: {
            title: 'macOS',
            req1: 'macOS 11.0 (Big Sur) or later',
            req2: '4 GB RAM minimum',
            req3: '100 MB free disk space'
          },
          linux: {
            title: 'Linux',
            req1: 'Ubuntu 20.04+ / Debian 11+ / Fedora 35+',
            req2: '4 GB RAM minimum',
            req3: '100 MB free disk space'
          },
          mobile: {
            title: 'Mobile',
            req1: 'Android 8.0+ or iOS 14.0+',
            req2: '2 GB RAM minimum',
            req3: '50 MB free disk space'
          }
        },
        unavailable: {
          title: 'Client Being Updated',
          message: 'The client is currently being updated. Please try again shortly.'
        },
        showRequirements: 'System Requirements',
        hideRequirements: 'Hide Requirements',
        noDownloads: 'No downloads available yet. Please check back later.',
        req: {
          os: 'OS',
          cpu: 'CPU',
          ram: 'RAM',
          disk: 'Disk',
          network: 'Network',
          processor: 'Processor',
          additional: 'Additional'
        },
        type: {
          link: 'Direct Download',
          store: 'App Store'
        }
      },

      // Profile Page
      profile: {
        title: 'Profile',
        subtitle: 'Manage your account settings',
        verified: 'Verified',
        unverified: 'Not Verified',
        fields: {
          username: 'Username',
          email: 'Email',
          company: 'Company',
          license: 'License',
          role: 'Role',
          memberSince: 'Member Since'
        },
        roles: {
          systemAdmin: 'System Admin',
          companyAdmin: 'Company Admin',
          user: 'User'
        },
        security: {
          title: 'Security',
          password: 'Password',
          passwordHint: 'Change your password to keep your account secure',
          changePassword: 'Change Password'
        },
        password: {
          title: 'Change Password',
          current: 'Current Password',
          new: 'New Password',
          confirm: 'Confirm New Password',
          save: 'Change Password',
          success: 'Password changed successfully',
          errors: {
            allFieldsRequired: 'All fields are required',
            passwordsDontMatch: 'New passwords do not match',
            passwordTooShort: 'Password must be at least 6 characters',
            failed: 'Failed to change password'
          }
        }
      },
      
      // Global Errors
      errors: {
        serverIssue: 'Server connection issue. Please try again later.',
        networkError: 'Network error. Please check your connection.'
      }
    }
  },
  tr: {
    translation: {
      // Navigation
      nav: {
        login: 'Giriş Yap',
        signup: 'Ücretsiz Başla',
        logout: 'Çıkış Yap'
      },
      
      // Landing Page
      landing: {
        hero: {
          badge: 'Kurumsal Güvenlik Çözümü',
          title: 'Ekipler için sakin ve güvenilir erişim.',
          description: 'Şirket kaynaklarına güvenli bir yol açın — merkezi kontrolle, günlük sürtünmeyi azaltarak.',
          cta: 'Ücretsiz dene',
          demo: 'Demo İzle',
          stat1: 'Uptime',
          stat2: 'Aktif Kullanıcı',
          stat3: 'Şifreleme'
        },
        features: {
          title: 'Gösteriş değil, operasyon.',
          subtitle: 'Şifreleme, hız ve erişim — gereksiz kalabalık olmadan.',
          security: {
            title: 'Askeri Seviye Şifreleme',
            description: 'AES-256 bit şifreleme standardı ile verileriniz tam güvende. Zero-log politikası ile gizliliğinizi koruyoruz.'
          },
          speed: {
            title: 'Ultra Hızlı Bağlantı',
            description: 'Optimize edilmiş sunucu altyapısı ile kesintisiz ve yüksek hızda veri aktarımı sağlıyoruz.'
          },
          global: {
            title: 'Global Ağ',
            description: 'Dünya çapında stratejik konumlardaki sunucularımız ile her yerden hızlı erişim imkanı.'
          }
        },
        platforms: {
          title: 'Tüm Platformlarda Kullanın',
          subtitle: 'Tüm cihazlarınızda kesintisiz koruma'
        },
        map: {
          title: 'Global Sunucu Ağı',
          subtitle: '50+ ülkede 5000+ sunucu ile hizmetinizdeyiz',
          countries: 'Ülke',
          servers: 'Sunucu',
          cities: 'Şehir'
        },
        devices: {
          title: 'Tüm Cihazlarınızı Bir Anda Koruyun',
          subtitle: 'Tek bir hesapla 10 cihaza kadar aynı anda bağlanın. Bilgisayarınızdan, telefonunuza tüm cihazlarınızda güvende kalın.',
          features: {
            unlimited: 'Sınırsız bant genişliği',
            auto: 'Otomatik bağlantı koruması',
            split: 'Split tunneling desteği',
            killswitch: 'Kill switch özelliği'
          }
        },
        useCases: {
          title: 'Kullanım Alanları',
          subtitle: 'Farklı senaryolar için esnek çözümler',
          case1: {
            title: 'Kurumsal Uzaktan Erişim',
            description: 'Evden çalışan personel için güvenli ve kesintisiz bağlantı imkanı'
          },
          case2: {
            title: 'Fabrika / Şube Bağlantıları',
            description: 'Farklı lokasyonlar arası güvenli veri aktarımı ve network yönetimi'
          },
          case3: {
            title: 'Mobil Erişim',
            description: 'Tablet ve telefon üzerinden kurumsal kaynaklara her yerden erişim'
          }
        },
        cta: {
          title: 'Güvenli ağınızı hemen kurmaya başlayın',
          description: '14 gün ücretsiz deneme ile tüm özellikleri keşfedin',
          button: 'Ücretsiz Başlayın'
        },
        footer: {
          tagline: 'Kurumsal seviyede güvenlik,\nyerli yazılım güvencesi',
          product: 'Ürün',
          features: 'Özellikler',
          pricing: 'Fiyatlandırma',
          docs: 'Dokümantasyon',
          company: 'Şirket',
          about: 'Hakkımızda',
          contact: 'İletişim',
          careers: 'Kariyer',
          support: 'Destek',
          help: 'Yardım Merkezi',
          status: 'Sistem Durumu',
          updates: 'Güncellemeler',
          rights: '© 2025 ArcVPN. Tüm hakları saklıdır.'
        }
      },
      
      // Login Page
      login: {
        title: 'Tekrar Hoşgeldiniz',
        subtitle: 'Hesabınıza giriş yapın',
        sessionExpired: 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın.',
        email: 'Email Adresi',
        emailPlaceholder: 'ornek@sirket.com',
        password: 'Şifre',
        passwordPlaceholder: 'Şifreniz',
        forgotPassword: 'Şifremi unuttum?',
        loginButton: 'Giriş Yap',
        loading: 'Giriş yapılıyor...',
        noAccount: 'Hesabınız yok mu?',
        signupLink: 'Ücretsiz Kayıt Olun',
        branding: {
          title: 'Tünele girin.',
          description: 'Peer, node ve şirket erişimini tek kontrol düzleminden yönetmek için giriş yapın.',
          stat1: 'Şirket',
          stat2: 'Çalışma Süresi',
          stat3: 'Sunucu'
        },
        forgot: {
          title: 'Erişimi sıfırla',
          description: 'Hesap e-postanızı girin. Sonraki adımda sizi yönlendireceğiz.',
          submit: 'Devam et',
          back: 'Girişe dön',
          needEmail: 'Lütfen e-posta adresinizi girin',
          success: 'Teşekkürler. {{email}} için bir hesap varsa ekibimiz erişimi yenilemenize yardımcı olabilir. İletişim sayfasından da yazabilirsiniz.',
          contact: 'Destek ile iletişim'
        },
        errors: {
          fillAll: 'Lütfen tüm alanları doldurun',
          failed: 'Giriş başarısız'
        },
        '2fa': {
          title: 'İki Faktörlü Doğrulama',
          description: 'E-posta adresinize gönderilen 6 haneli kodu girin',
          back: 'Geri',
          invalidCode: 'Lütfen 6 haneli kodu girin',
          verifyError: 'Doğrulama başarısız',
          resendError: 'Kod gönderilemedi',
          verifying: 'Doğrulanıyor...',
          verify: 'Doğrula',
          noCode: 'Kod almadınız mı?',
          resendIn: 'Tekrar gönder ({{seconds}}sn)',
          resend: 'Tekrar Gönder'
        }
      },
      
      // Signup Page
      signup: {
        title: 'Hesap Oluştur',
        subtitle: 'Hemen başlamak için bilgilerinizi girin',
        fullName: 'Ad Soyad',
        fullNamePlaceholder: 'Adınız ve soyadınız',
        email: 'Email Adresi',
        emailPlaceholder: 'ornek@sirket.com',
        companyName: 'Şirket Adı',
        companyNamePlaceholder: 'Şirket adınız',
        inviteCodeToggle: 'Şirket davet kodum var',
        inviteCode: 'Davet Kodu',
        inviteCodePlaceholder: 'Davet kodunuzu girin',
        password: 'Şifre',
        passwordPlaceholder: 'En az 8 karakter',
        confirmPassword: 'Şifre Tekrar',
        confirmPasswordPlaceholder: 'Şifrenizi tekrar girin',
        signupButton: 'Hesap Oluştur',
        loading: 'İşleniyor...',
        hasAccount: 'Zaten hesabınız var mı?',
        loginLink: 'Giriş Yapın',
        branding: {
          title: 'Güvenli Ağınıza Hoş Geldiniz',
          description: 'Kurumsal seviyede VPN güvenliği, tek bir platformda. Ekibinizi korumaya bugün başlayın.',
          feature1: '14 gün ücretsiz deneme',
          feature2: 'Kredi kartı gerekmez',
          feature3: 'İstediğiniz zaman iptal edin'
        },
        errors: {
          fillAll: 'Lütfen tüm alanları doldurun',
          companyRequired: 'Şirket adı gerekli (veya davet kodunuz varsa kullanın)',
          inviteRequired: 'Lütfen davet kodunu girin',
          passwordMismatch: 'Şifreler eşleşmiyor',
          passwordLength: 'Şifre en az 8 karakter olmalıdır',
          failed: 'Kayıt işlemi başarısız'
        }
      },
      
      // Public Pages
      features: {
        title: 'Özellikler',
        subtitle: 'Güvenli bağlantı için ihtiyacınız olan her şey',
        list: {
          encryption: {
            title: 'AES-256 Bit Şifreleme',
            description: 'Askeri seviye şifreleme standardı ile verileriniz tam güvende. Zero-log politikası ile gizliliğinizi garanti ediyoruz.',
            detail1: 'Uçtan uca şifreleme',
            detail2: 'Mükemmel ileri gizlilik',
            detail3: 'Sıfır kayıt politikası'
          },
          speed: {
            title: 'Ultra Hızlı Bağlantı',
            description: 'Optimize edilmiş sunucu altyapısı ile kesintisiz ve yüksek hızda veri aktarımı.',
            detail1: '10Gbps sunucular',
            detail2: 'Düşük gecikme',
            detail3: 'Sınırsız bant genişliği'
          },
          global: {
            title: 'Global Sunucu Ağı',
            description: '50+ ülkede 5000+ sunucu ile dünyanın her yerinden hızlı erişim.',
            detail1: '5000+ sunucu',
            detail2: '50+ ülke',
            detail3: '100+ şehir'
          },
          multiplatform: {
            title: 'Çoklu Platform Desteği',
            description: 'Windows, macOS, Linux, iOS ve Android için native uygulamalar.',
            detail1: '10 cihaza kadar',
            detail2: 'Tüm platformlar',
            detail3: 'Kolay kurulum'
          },
          mfa: {
            title: 'Çok Faktörlü Kimlik Doğrulama',
            description: 'Çok faktörlü kimlik doğrulama ile ek güvenlik katmanı.',
            detail1: '2FA/MFA desteği',
            detail2: 'Donanım token',
            detail3: 'Biyometrik giriş'
          },
          admin: {
            title: 'Merkezi Yönetim Paneli',
            description: 'Web tabanlı admin paneli ile tüm kullanıcılarınızı tek yerden yönetin.',
            detail1: 'Kullanıcı yönetimi',
            detail2: 'Detaylı loglar',
            detail3: 'Gerçek zamanlı izleme'
          },
          killswitch: {
            title: 'Otomatik Kill Switch',
            description: 'VPN bağlantısı kesildiğinde internet erişimini otomatik kapatır.',
            detail1: 'Veri güvenliği',
            detail2: 'IP sızıntı koruması',
            detail3: 'DNS sızıntı koruması'
          },
          split: {
            title: 'Split Tunneling',
            description: 'Hangi uygulamaların VPN kullanacağını seçin.',
            detail1: 'Uygulama bazlı',
            detail2: 'Esnek konfigürasyon',
            detail3: 'Performans optimizasyonu'
          },
          local: {
            title: 'Yerli Yazılım',
            description: 'Türkiye\'de geliştirildi, yerli ve milli yazılım güvencesi.',
            detail1: 'Yerel destek',
            detail2: 'Hızlı müdahale',
            detail3: 'Özel çözümler'
          }
        },
        cta: {
          title: 'Güvenli Ağınızı Hemen Kurun',
          description: '14 gün ücretsiz deneme, kredi kartı gerekmez'
        }
      },
      pricing: {
        title: 'Fiyatlandırma',
        subtitle: 'İşletmeniz için en uygun planı seçin. Tüm planlar 14 gün ücretsiz deneme ile başlar.',
        popular: 'En Popüler',
        startBtn: 'Hemen Başla',
        contactBtn: 'İletişime Geç',
        plans: {
          starter: {
            name: 'Başlangıç',
            period: 'ay',
            description: 'Küçük ekipler için ideal başlangıç paketi',
            feature1: '5 kullanıcıya kadar',
            feature2: 'Temel VPN özellikleri',
            feature3: 'Email destek',
            feature4: '50 GB/ay trafik',
            feature5: '10 cihaz bağlantısı',
            feature6: 'Temel raporlama'
          },
          professional: {
            name: 'Profesyonel',
            period: 'ay',
            description: 'Büyüyen şirketler için kapsamlı çözüm',
            feature1: '25 kullanıcıya kadar',
            feature2: 'Tüm VPN özellikleri',
            feature3: 'Öncelikli destek',
            feature4: 'Sınırsız trafik',
            feature5: 'Sınırsız cihaz',
            feature6: 'Gelişmiş raporlama',
            feature7: 'API erişimi',
            feature8: 'SSO entegrasyonu'
          },
          enterprise: {
            name: 'Kurumsal',
            price: 'Özel',
            period: 'teklif',
            description: 'Kurumsal şirketler için özel çözümler',
            feature1: 'Sınırsız kullanıcı',
            feature2: 'Tüm özellikler',
            feature3: '7/24 destek',
            feature4: 'Özel sunucu',
            feature5: 'SLA garantisi',
            feature6: 'Özel entegrasyonlar',
            feature7: 'Eğitim ve danışmanlık',
            feature8: 'Yerinde kurulum'
          }
        },
        faq: {
          title: 'Sık Sorulan Sorular',
          q1: 'Ücretsiz deneme nasıl çalışır?',
          a1: '14 gün boyunca tüm özellikleri ücretsiz deneyebilirsiniz. Kredi kartı bilgisi gerekmez.',
          q2: 'İstediğim zaman iptal edebilir miyim?',
          a2: 'Evet, herhangi bir taahhüt olmadan istediğiniz zaman iptal edebilirsiniz.',
          q3: 'Fatura hangi para birimi ile kesilir?',
          a3: 'Tüm faturalar Türk Lirası (₺) olarak kesilir. KDV dahildir.',
          q4: 'Planımı yükseltebilir miyim?',
          a4: 'Evet, istediğiniz zaman planınızı yükseltebilir veya düşürebilirsiniz.'
        }
      },
      docs: {
        title: 'Dokümantasyon',
        subtitle: 'Kapsamlı rehberler ve API referansları'
      },
      about: {
        title: 'Hakkımızda',
        subtitle: 'Güvenli ağların öncüsü, yerli ve milli VPN çözümü'
      },
      contact: {
        title: 'İletişim',
        subtitle: 'Sorularınız mı var? Ekibimiz size yardımcı olmak için burada.',
        formSubmitted: 'Form gönderildi! (Backend entegrasyonu gerekli)'
      },
      careers: {
        title: 'Kariyer',
        subtitle: 'Büyüyen ekibimize katılın'
      },
      help: {
        title: 'Yardım Merkezi',
        subtitle: 'Sık sorulan sorulara yanıt bulun'
      },
      status: {
        title: 'Sistem Durumu',
        subtitle: 'Servislerimizin gerçek zamanlı durumu'
      },
      updates: {
        title: 'Güncellemeler',
        subtitle: 'En son özellikler ve iyileştirmeler'
      },
      
      // Email Verification
      verification: {
        title: 'Email Doğrulama',
        subtitle: 'Hesabınızı aktifleştirmek için email adresinizi doğrulayın',
        instruction: 'adresine bir doğrulama e-postası gönderdik. Lütfen e-postanızdaki bağlantıya tıklayın veya aşağıya kodu girin.',
        codeLabel: 'Doğrulama Kodu',
        codePlaceholder: 'Doğrulama kodunu girin',
        verifyBtn: 'Doğrula',
        verifying: 'Doğrulanıyor...',
        or: 'veya',
        didntReceive: 'E-posta almadınız mı?',
        resendBtn: 'Tekrar Gönder',
        sending: 'Gönderiliyor...',
        checkBtn: 'Onayladım, Kontrol Et',
        backToDashboard: 'Dashboard\'a Dön',
        backToLogin: 'Giriş Sayfasına Dön',
        success: 'Email adresiniz başarıyla doğrulandı! Yönlendiriliyorsunuz...',
        resent: 'Doğrulama e-postası tekrar gönderildi!',
        checking: 'Doğrulama durumu kontrol ediliyor...',
        errors: {
          emptyCode: 'Lütfen doğrulama kodunu girin',
          failed: 'Doğrulama başarısız',
          noUser: 'Kullanıcı bilgisi bulunamadı',
          sendFailed: 'E-posta gönderilemedi',
          refresh: 'Lütfen sayfayı yenileyerek tekrar deneyin'
        }
      },
      
      // Dashboard
      dashboard: {
        welcome: 'Hoş geldiniz',
        subtitle: 'VPN ağınıza genel bakış',
        recentActivities: 'Son Aktiviteler',
        systemInfo: 'Sistem Bilgileri',
        quickAccess: 'Hızlı Erişim',
        stats: {
          connections: 'Aktif Bağlantılar',
          users: 'Toplam Kullanıcı',
          data: 'Veri Transferi',
          status: 'Sistem Durumu',
          online: 'Çevrimiçi'
        },
        activities: {
          connected: 'Bağlantı başlatıldı',
          userAdded: 'Yeni kullanıcı eklendi',
          disconnected: 'Bağlantı sonlandırıldı',
          systemUpdated: 'Sistem güncellendi'
        },
        system: {
          version: 'Sunucu Sürümü',
          uptime: 'Çalışma Süresi',
          cpu: 'CPU Kullanımı',
          memory: 'Bellek Kullanımı',
          disk: 'Disk Kullanımı'
        },
        time: {
          minutesAgo: '{{count}} dakika önce',
          hoursAgo: '{{count}} saat önce',
          days: 'gün',
          hoursShort: 'saat'
        },
        actions: {
          addUser: 'Yeni Kullanıcı Ekle',
          viewReports: 'Raporları Görüntüle',
          settings: 'Sistem Ayarları'
        },
        verification: {
          title: 'Email Doğrulama Gerekli',
          message: 'Dashboard\'a erişmek için lütfen email adresinizi doğrulayın.',
          button: 'Şimdi Doğrula'
        }
      },

      // System Admin
      systemAdmin: {
        title: 'Sistem Yönetimi',
        subtitle: 'ARCVPN sistem yönetim paneli',
        back: 'Geri',
        menu: {
          companies: {
            title: 'Company Yönetimi',
            description: 'Şirket bilgileri, lisanslar ve kullanıcı yönetimi'
          },
          servers: {
            title: 'VPN Sunucular',
            description: 'VPN sunucu yönetimi, lokasyonlar ve node konfigürasyonu'
          },
          settings: {
            title: 'Sistem Ayarları',
            description: 'Güvenlik, ağ, bildirim ve sistem konfigürasyonları'
          },
          logs: {
            title: 'Sistem Logları',
            description: 'Sistem logları, aktivite takibi ve monitoring'
          }
        },
        servers: {
          title: 'VPN Sunucular',
          subtitle: 'Sunucu yönetimi ve izleme',
          stats: {
            totalServers: 'Toplam Sunucu',
            onlineServers: 'Online Sunucu',
            activeUsers: 'Aktif Kullanıcı',
            avgLoad: 'Ortalama Yük'
          },
          filter: {
            label: 'Ülke Filtresi',
            all: 'Tüm Ülkeler'
          },
          pendingServers: 'Bekleyen Sunucular',
          addServer: 'Yeni Sunucu Ekle',
          card: {
            load: 'Yük',
            users: 'Kullanıcılar',
            monitor: 'İzle',
            settings: 'Ayarlar',
            restart: 'Yeniden Başlat',
            approve: 'Onayla',
            reject: 'Reddet'
          },
          status: {
            online: 'Online',
            offline: 'Offline',
            maintenance: 'Bakım'
          },
          agentStatus: {
            connected: 'Agent Bağlı',
            disconnected: 'Agent Bağlı Değil',
            pending: 'Onay Bekliyor'
          },
          empty: {
            title: 'Henüz sunucu yok',
            description: 'Yeni bir sunucu eklemek için "Yeni Sunucu Ekle" butonunu kullanın.'
          },
          loading: 'Sunucular yükleniyor...',
          error: 'Sunucular yüklenirken bir hata oluştu',
          retry: 'Tekrar Dene',
          pendingModal: {
            title: 'Onay Bekleyen Sunucular',
            noPending: 'Bekleyen sunucu yok.',
            approve: 'Onayla',
            reject: 'Reddet'
          }
        }
      },

      // Sidebar
      sidebar: {
        dashboard: 'Panel',
        connections: 'Bağlantılar',
        downloads: 'İndirmeler',
        company: 'Şirketim',
        admin: 'Sistem Yönetimi',
        profile: 'Profil',
        logout: 'Çıkış Yap'
      },

      // Connections Page
      connections: {
        title: 'Bağlantılar',
        myConnections: 'Bağlantılarım',
        companyConnections: 'Şirket Bağlantıları',
        activeConnections: 'Aktif Bağlantı',
        totalData: 'Toplam Veri',
        avgDuration: 'Ort. Süre',
        loading: 'Yükleniyor...',
        noData: 'Aktif bağlantı bulunmamaktadır',
        table: {
          user: 'Kullanıcı',
          server: 'Sunucu',
          ipAddress: 'IP Adresi',
          location: 'Konum',
          startTime: 'Başlangıç',
          duration: 'Süre',
          data: 'Veri',
          status: 'Durum'
        },
        status: {
          active: 'Aktif',
          disconnected: 'Kesildi'
        },
        footer: 'Toplam {{count}} bağlantı gösteriliyor',
        time: {
          hours: 's',
          minutes: 'dk'
        }
      },

      // Downloads Page
      downloads: {
        title: 'ArcVPN İndir',
        subtitle: 'Platformunuzu seçin ve gizliliğinizi korumaya başlayın',
        download: 'İndir',
        comingSoon: '{{platform}} istemcisi yakında!',
        platforms: {
          windows: {
            name: 'Windows',
            description: 'Windows 10/11 (64-bit)'
          },
          macos: {
            name: 'macOS',
            description: 'macOS 11.0 ve üzeri'
          },
          linux: {
            name: 'Linux',
            description: 'Ubuntu, Debian, Fedora'
          },
          android: {
            name: 'Android',
            description: 'Android 8.0 ve üzeri'
          },
          ios: {
            name: 'iOS',
            description: 'iOS 14.0 ve üzeri'
          }
        },
        guide: {
          title: 'Kurulum Rehberi',
          step1: {
            title: 'İndirin',
            description: 'Platformunuzu seçin ve yükleyiciyi indirin'
          },
          step2: {
            title: 'Kurun',
            description: 'Yükleyiciyi çalıştırın ve kurulum sihirbazını takip edin'
          },
          step3: {
            title: 'Giriş Yapın',
            description: 'Uygulamayı açın ve kimlik bilgilerinizle giriş yapın'
          },
          step4: {
            title: 'Bağlanın',
            description: 'Bir sunucu seçin ve güvenle gezinmeye başlayın'
          }
        },
        requirements: {
          title: 'Sistem Gereksinimleri',
          windows: {
            title: 'Windows',
            req1: 'Windows 10 veya üzeri (64-bit)',
            req2: 'Minimum 4 GB RAM',
            req3: '100 MB boş disk alanı'
          },
          macos: {
            title: 'macOS',
            req1: 'macOS 11.0 (Big Sur) veya üzeri',
            req2: 'Minimum 4 GB RAM',
            req3: '100 MB boş disk alanı'
          },
          linux: {
            title: 'Linux',
            req1: 'Ubuntu 20.04+ / Debian 11+ / Fedora 35+',
            req2: 'Minimum 4 GB RAM',
            req3: '100 MB boş disk alanı'
          },
          mobile: {
            title: 'Mobil',
            req1: 'Android 8.0+ veya iOS 14.0+',
            req2: 'Minimum 2 GB RAM',
            req3: '50 MB boş disk alanı'
          }
        },
        unavailable: {
          title: 'İstemci Güncelleniyor',
          message: 'İstemci şu anda güncellenmektedir. Lütfen kısa süre sonra tekrar deneyin.'
        },
        showRequirements: 'Sistem Gereksinimleri',
        hideRequirements: 'Gereksinimleri Gizle',
        noDownloads: 'Henüz indirme mevcut değil. Lütfen daha sonra tekrar kontrol edin.',
        req: {
          os: 'İşletim Sistemi',
          cpu: 'İşlemci',
          ram: 'RAM',
          disk: 'Disk',
          network: 'Ağ',
          processor: 'İşlemci',
          additional: 'Ek Gereksinimler'
        },
        type: {
          link: 'Doğrudan İndirme',
          store: 'Uygulama Mağazası'
        }
      },

      // Profile Page
      profile: {
        title: 'Profil',
        subtitle: 'Hesap ayarlarınızı yönetin',
        verified: 'Doğrulandı',
        unverified: 'Doğrulanmadı',
        fields: {
          username: 'Kullanıcı Adı',
          email: 'E-posta',
          company: 'Şirket',
          license: 'Lisans',
          role: 'Rol',
          memberSince: 'Üyelik Tarihi'
        },
        roles: {
          systemAdmin: 'Sistem Yöneticisi',
          companyAdmin: 'Şirket Yöneticisi',
          user: 'Kullanıcı'
        },
        security: {
          title: 'Güvenlik',
          password: 'Şifre',
          passwordHint: 'Hesabınızı güvende tutmak için şifrenizi değiştirin',
          changePassword: 'Şifre Değiştir'
        },
        password: {
          title: 'Şifre Değiştir',
          current: 'Mevcut Şifre',
          new: 'Yeni Şifre',
          confirm: 'Yeni Şifre (Tekrar)',
          save: 'Şifreyi Değiştir',
          success: 'Şifre başarıyla değiştirildi',
          errors: {
            allFieldsRequired: 'Tüm alanlar zorunludur',
            passwordsDontMatch: 'Yeni şifreler eşleşmiyor',
            passwordTooShort: 'Şifre en az 6 karakter olmalıdır',
            failed: 'Şifre değiştirilemedi'
          }
        }
      },
      
      // Global Errors
      errors: {
        serverIssue: 'Sunucu bağlantı sorunu. Lütfen daha sonra tekrar deneyin.',
        networkError: 'Ağ hatası. Lütfen bağlantınızı kontrol edin.'
      }
    }
  },
  fr: {
    translation: {
      // Navigation
      nav: {
        login: 'Connexion',
        signup: 'Commencer',
        logout: 'Déconnexion'
      },
      
      // Landing Page
      landing: {
        hero: {
          badge: 'Solution de Sécurité d\'Entreprise',
          title: 'Un accès calme et fiable pour chaque équipe.',
          description: 'Offrez un chemin sécurisé vers les ressources de l\'entreprise — avec un contrôle central et moins de friction au quotidien.',
          cta: 'Essai gratuit',
          demo: 'Voir la Démo',
          stat1: 'Disponibilité',
          stat2: 'Utilisateurs Actifs',
          stat3: 'Cryptage'
        },
        features: {
          title: 'Conçu pour l\'opération, pas le spectacle.',
          subtitle: 'Chiffrement, vitesse et portée — sans le bruit.',
          security: {
            title: 'Cryptage de Niveau Militaire',
            description: 'Vos données sont entièrement sécurisées avec la norme de cryptage AES-256 bit. Nous protégeons votre confidentialité avec une politique zéro journal.'
          },
          speed: {
            title: 'Connexion Ultra-Rapide',
            description: 'Nous fournissons un transfert de données fluide et à haute vitesse avec une infrastructure de serveurs optimisée.'
          },
          global: {
            title: 'Réseau Global',
            description: 'Accès rapide de n\'importe où avec nos serveurs situés stratégiquement dans le monde entier.'
          }
        },
        platforms: {
          title: 'Utiliser sur Toutes les Plateformes',
          subtitle: 'Protection transparente sur tous vos appareils'
        },
        map: {
          title: 'Réseau de Serveurs Mondial',
          subtitle: 'Au service de 5000+ serveurs dans 50+ pays',
          countries: 'Pays',
          servers: 'Serveurs',
          cities: 'Villes'
        },
        devices: {
          title: 'Protégez Tous Vos Appareils en Même Temps',
          subtitle: 'Connectez jusqu\'à 10 appareils simultanément avec un seul compte. Restez en sécurité sur tous vos appareils, de votre ordinateur à votre téléphone.',
          features: {
            unlimited: 'Bande passante illimitée',
            auto: 'Protection de connexion automatique',
            split: 'Support du tunneling divisé',
            killswitch: 'Fonction Kill Switch'
          }
        },
        useCases: {
          title: 'Cas d\'Utilisation',
          subtitle: 'Solutions flexibles pour différents scénarios',
          case1: {
            title: 'Accès à Distance d\'Entreprise',
            description: 'Connexion sécurisée et ininterrompue pour les employés travaillant à distance'
          },
          case2: {
            title: 'Connexions Usine / Succursale',
            description: 'Transfert de données sécurisé et gestion réseau entre différents emplacements'
          },
          case3: {
            title: 'Accès Mobile',
            description: 'Accédez aux ressources d\'entreprise depuis n\'importe où via tablette et téléphone'
          }
        },
        cta: {
          title: 'Commencez à Construire Votre Réseau Sécurisé Maintenant',
          description: 'Découvrez toutes les fonctionnalités avec un essai gratuit de 14 jours',
          button: 'Commencer'
        },
        footer: {
          tagline: 'Sécurité de niveau entreprise,\ngarantie logicielle turque',
          product: 'Produit',
          features: 'Fonctionnalités',
          pricing: 'Tarification',
          docs: 'Documentation',
          company: 'Entreprise',
          about: 'À Propos',
          contact: 'Contact',
          careers: 'Carrières',
          support: 'Support',
          help: 'Centre d\'Aide',
          status: 'État du Système',
          updates: 'Mises à Jour',
          rights: '© 2025 ArcVPN. Tous droits réservés.'
        }
      },
      
      // Login Page
      login: {
        title: 'Bon Retour',
        subtitle: 'Connectez-vous à votre compte',
        sessionExpired: 'Votre session a expiré. Veuillez vous reconnecter.',
        email: 'Adresse Email',
        emailPlaceholder: 'exemple@entreprise.com',
        password: 'Mot de Passe',
        passwordPlaceholder: 'Votre mot de passe',
        forgotPassword: 'Mot de passe oublié?',
        loginButton: 'Se Connecter',
        loading: 'Connexion en cours...',
        noAccount: 'Pas de compte?',
        signupLink: 'Inscription gratuite',
        branding: {
          title: 'Entrez dans le tunnel.',
          description: 'Connectez-vous pour gérer peers, nœuds et accès entreprise depuis un seul plan de contrôle.',
          stat1: 'Entreprises',
          stat2: 'Disponibilité',
          stat3: 'Serveurs'
        },
        forgot: {
          title: 'Réinitialiser l\'accès',
          description: 'Entrez l\'e-mail de votre compte. Nous vous guiderons pour la suite.',
          submit: 'Continuer',
          back: 'Retour à la connexion',
          needEmail: 'Veuillez entrer votre adresse e-mail',
          success: 'Merci. Si un compte existe pour {{email}}, notre équipe peut vous aider à restaurer l\'accès. Vous pouvez aussi nous écrire via Contact.',
          contact: 'Contacter le support'
        },
        errors: {
          fillAll: 'Veuillez remplir tous les champs',
          failed: 'Échec de la connexion'
        },
        '2fa': {
          title: 'Authentification à Deux Facteurs',
          description: 'Entrez le code à 6 chiffres envoyé à votre adresse email',
          back: 'Retour',
          invalidCode: 'Veuillez entrer le code à 6 chiffres',
          verifyError: 'Échec de la vérification',
          resendError: 'Échec de l\'envoi du code',
          verifying: 'Vérification...',
          verify: 'Vérifier',
          noCode: 'Vous n\'avez pas reçu le code?',
          resendIn: 'Renvoyer ({{seconds}}s)',
          resend: 'Renvoyer le Code'
        }
      },
      
      // Signup Page
      signup: {
        title: 'Créer un Compte',
        subtitle: 'Entrez vos informations pour commencer',
        fullName: 'Nom Complet',
        fullNamePlaceholder: 'Votre nom complet',
        email: 'Adresse Email',
        emailPlaceholder: 'exemple@entreprise.com',
        companyName: 'Nom de l\'Entreprise',
        companyNamePlaceholder: 'Nom de votre entreprise',
        inviteCodeToggle: 'J\'ai un code d\'invitation d\'entreprise',
        inviteCode: 'Code d\'Invitation',
        inviteCodePlaceholder: 'Entrez votre code d\'invitation',
        password: 'Mot de Passe',
        passwordPlaceholder: 'Au moins 8 caractères',
        confirmPassword: 'Confirmer le Mot de Passe',
        confirmPasswordPlaceholder: 'Ressaisissez votre mot de passe',
        signupButton: 'Créer un Compte',
        loading: 'Traitement...',
        hasAccount: 'Vous avez déjà un compte?',
        loginLink: 'Se Connecter',
        branding: {
          title: 'Bienvenue dans Votre Réseau Sécurisé',
          description: 'Sécurité VPN de niveau entreprise sur une seule plateforme. Commencez à protéger votre équipe dès aujourd\'hui.',
          feature1: 'Essai gratuit de 14 jours',
          feature2: 'Aucune carte de crédit requise',
          feature3: 'Annulez à tout moment'
        },
        errors: {
          fillAll: 'Veuillez remplir tous les champs',
          companyRequired: 'Nom d\'entreprise requis (ou utilisez un code d\'invitation)',
          inviteRequired: 'Veuillez entrer le code d\'invitation',
          passwordMismatch: 'Les mots de passe ne correspondent pas',
          passwordLength: 'Le mot de passe doit contenir au moins 8 caractères',
          failed: 'Échec de l\'inscription'
        }
      },
      
      // Public Pages
      features: {
        title: 'Fonctionnalités',
        subtitle: 'Tout ce dont vous avez besoin pour une connectivité sécurisée',
        list: {
          encryption: {
            title: 'Cryptage AES-256 Bit',
            description: 'Vos données sont entièrement sécurisées avec un cryptage de niveau militaire. Nous garantissons votre confidentialité avec une politique zéro journal.',
            detail1: 'Cryptage de bout en bout',
            detail2: 'Confidentialité persistante parfaite',
            detail3: 'Politique zéro journal'
          },
          speed: {
            title: 'Connexion Ultra-Rapide',
            description: 'Transfert de données fluide et à haute vitesse avec une infrastructure serveur optimisée.',
            detail1: 'Serveurs 10Gbps',
            detail2: 'Faible latence',
            detail3: 'Bande passante illimitée'
          },
          global: {
            title: 'Réseau de Serveurs Mondial',
            description: 'Accès rapide depuis n\'importe où avec 5000+ serveurs dans 50+ pays.',
            detail1: '5000+ serveurs',
            detail2: '50+ pays',
            detail3: '100+ villes'
          },
          multiplatform: {
            title: 'Support Multi-Plateforme',
            description: 'Applications natives pour Windows, macOS, Linux, iOS et Android.',
            detail1: 'Jusqu\'à 10 appareils',
            detail2: 'Toutes les plateformes',
            detail3: 'Configuration facile'
          },
          mfa: {
            title: 'Authentification Multi-Facteurs',
            description: 'Couche de sécurité supplémentaire avec authentification multi-facteurs.',
            detail1: 'Support 2FA/MFA',
            detail2: 'Jeton matériel',
            detail3: 'Connexion biométrique'
          },
          admin: {
            title: 'Panneau de Gestion Centralisé',
            description: 'Gérez tous vos utilisateurs depuis un seul endroit avec le panneau d\'administration web.',
            detail1: 'Gestion des utilisateurs',
            detail2: 'Journaux détaillés',
            detail3: 'Surveillance en temps réel'
          },
          killswitch: {
            title: 'Kill Switch Automatique',
            description: 'Coupe automatiquement l\'accès Internet si la connexion VPN tombe.',
            detail1: 'Sécurité des données',
            detail2: 'Protection contre les fuites IP',
            detail3: 'Protection contre les fuites DNS'
          },
          split: {
            title: 'Tunneling Divisé',
            description: 'Choisissez quelles applications utilisent le VPN.',
            detail1: 'Basé sur les applications',
            detail2: 'Configuration flexible',
            detail3: 'Optimisation des performances'
          },
          local: {
            title: 'Logiciel Turc',
            description: 'Développé en Turquie, garantie logicielle domestique et nationale.',
            detail1: 'Support local',
            detail2: 'Réponse rapide',
            detail3: 'Solutions personnalisées'
          }
        },
        cta: {
          title: 'Démarrez Votre Réseau Sécurisé Maintenant',
          description: 'Essai gratuit de 14 jours, aucune carte de crédit requise'
        }
      },
      pricing: {
        title: 'Tarification',
        subtitle: 'Choisissez le meilleur plan pour votre entreprise. Tous les plans commencent par un essai gratuit de 14 jours.',
        popular: 'Plus Populaire',
        startBtn: 'Commencer',
        contactBtn: 'Nous Contacter',
        plans: {
          starter: {
            name: 'Débutant',
            period: 'mois',
            description: 'Package de démarrage idéal pour les petites équipes',
            feature1: 'Jusqu\'à 5 utilisateurs',
            feature2: 'Fonctionnalités VPN de base',
            feature3: 'Support par email',
            feature4: '50 GB/mois de trafic',
            feature5: '10 connexions d\'appareils',
            feature6: 'Rapports de base'
          },
          professional: {
            name: 'Professionnel',
            period: 'mois',
            description: 'Solution complète pour les entreprises en croissance',
            feature1: 'Jusqu\'à 25 utilisateurs',
            feature2: 'Toutes les fonctionnalités VPN',
            feature3: 'Support prioritaire',
            feature4: 'Trafic illimité',
            feature5: 'Appareils illimités',
            feature6: 'Rapports avancés',
            feature7: 'Accès API',
            feature8: 'Intégration SSO'
          },
          enterprise: {
            name: 'Entreprise',
            price: 'Personnalisé',
            period: 'devis',
            description: 'Solutions personnalisées pour les grandes entreprises',
            feature1: 'Utilisateurs illimités',
            feature2: 'Toutes les fonctionnalités',
            feature3: 'Support 24/7',
            feature4: 'Serveur dédié',
            feature5: 'Garantie SLA',
            feature6: 'Intégrations personnalisées',
            feature7: 'Formation et conseil',
            feature8: 'Installation sur site'
          }
        },
        faq: {
          title: 'Questions Fréquemment Posées',
          q1: 'Comment fonctionne l\'essai gratuit?',
          a1: 'Vous pouvez essayer toutes les fonctionnalités gratuitement pendant 14 jours. Aucune carte de crédit requise.',
          q2: 'Puis-je annuler à tout moment?',
          a2: 'Oui, vous pouvez annuler à tout moment sans aucun engagement.',
          q3: 'En quelle devise les factures sont-elles émises?',
          a3: 'Toutes les factures sont émises en Livre Turque (₺). TVA incluse.',
          q4: 'Puis-je améliorer mon plan?',
          a4: 'Oui, vous pouvez améliorer ou rétrograder votre plan à tout moment.'
        }
      },
      docs: {
        title: 'Documentation',
        subtitle: 'Guides complets et références API'
      },
      about: {
        title: 'À Propos',
        subtitle: 'Construire l\'avenir de la connectivité sécurisée'
      },
      contact: {
        title: 'Contact',
        subtitle: 'Contactez notre équipe',
        formSubmitted: 'Formulaire soumis! (Intégration backend requise)'
      },
      careers: {
        title: 'Carrières',
        subtitle: 'Rejoignez notre équipe en croissance'
      },
      help: {
        title: 'Centre d\'Aide',
        subtitle: 'Trouvez des réponses aux questions courantes'
      },
      status: {
        title: 'État du Système',
        subtitle: 'État en temps réel de nos services'
      },
      updates: {
        title: 'Mises à Jour',
        subtitle: 'Dernières fonctionnalités et améliorations'
      },
      
      // Email Verification
      verification: {
        title: 'Vérification Email',
        subtitle: 'Vérifiez votre adresse email pour activer votre compte',
        instruction: 'Nous avons envoyé un email de vérification. Veuillez cliquer sur le lien dans l\'email ou entrer le code ci-dessous.',
        codeLabel: 'Code de Vérification',
        codePlaceholder: 'Entrez le code de vérification',
        verifyBtn: 'Vérifier',
        verifying: 'Vérification...',
        or: 'ou',
        didntReceive: 'Vous n\'avez pas reçu l\'email?',
        resendBtn: 'Renvoyer',
        sending: 'Envoi...',
        checkBtn: 'J\'ai Confirmé, Vérifier',
        backToDashboard: 'Retour au Tableau de Bord',
        backToLogin: 'Retour à la Connexion',
        success: 'Email vérifié avec succès! Redirection...',
        resent: 'Email de vérification renvoyé!',
        checking: 'Vérification du statut...',
        errors: {
          emptyCode: 'Veuillez entrer le code de vérification',
          failed: 'Échec de la vérification',
          noUser: 'Informations utilisateur introuvables',
          sendFailed: 'Impossible d\'envoyer l\'email',
          refresh: 'Veuillez rafraîchir la page et réessayer'
        }
      },
      
            // Dashboard
      dashboard: {
        welcome: 'Bienvenue',
        subtitle: 'Aperçu de votre réseau VPN',
        recentActivities: 'Activités Récentes',
        systemInfo: 'Informations Système',
        quickAccess: 'Accès Rapide',
        stats: {
          connections: 'Connexions Actives',
          users: 'Utilisateurs Total',
          data: 'Transfert de Données',
          status: 'État du Système',
          online: 'En ligne'
        },
        activities: {
          connected: 'Connexion initiée',
          userAdded: 'Nouvel utilisateur ajouté',
          disconnected: 'Connexion terminée',
          systemUpdated: 'Système mis à jour'
        },
        system: {
          version: 'Version Serveur',
          uptime: 'Temps de Fonctionnement',
          cpu: 'Utilisation CPU',
          memory: 'Utilisation Mémoire',
          disk: 'Utilisation Disque'
        },
        time: {
          minutesAgo: 'Il y a {{count}} minutes',
          hoursAgo: 'Il y a {{count}} heures',
          days: 'jours',
          hoursShort: 'heures'
        },
        actions: {
          addUser: 'Ajouter Utilisateur',
          viewReports: 'Voir Rapports',
          settings: 'Paramètres Système'
        },
        verification: {
          title: 'Vérification Email Requise',
          message: 'Veuillez vérifier votre email pour accéder au tableau de bord.',
          button: 'Vérifier Maintenant'
        }
      },
      
      // Global Errors
      errors: {
        serverIssue: 'Problème de connexion au serveur. Veuillez réessayer plus tard.',
        networkError: 'Erreur réseau. Veuillez vérifier votre connexion.'
      }
    }
  }
};

resources.en.translation = deepMerge(resources.en.translation, uiPack.en);
resources.tr.translation = deepMerge(resources.tr.translation, uiPack.tr);
resources.fr.translation = deepMerge(resources.fr.translation, uiPack.fr);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

export default i18n;
